import React, {useEffect, useState, useCallback} from "react";
import {useTranslation} from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { setPage } from "../store/slices/pageSlice";
import { setSelectingPromoType } from "../store/slices/promoSelectModeSlice";
import SegmentSwitch from "../components/Home/HomeSwitch/SegmentSwitch";
import more from '../assets/icon/dot-grid.png';
import Profile from "../components/Home/Profile/Profile";
import NewPromoButton from "../components/Home/items/NewPromoButton";
import PromoList from "../components/Home/PromoList";
import BonusCarousel from "../components/BonusCarousel/BonusCarousel";
import referralApi from "../api/referralApi";
import bonusApi from "../api/bonusApi";
import useTelegram from "../hooks/useTelegram";
import Drops from "../components/drops/Drops";
import CrystalsIcon from '../assets/icon/bottomBar/Crystals.png';
import CCIcon from '../assets/icon/bottomBar/cc.png';
import CryptoIcon from '../assets/icon/bottomBar/crypto.png';

export default function HomeLayout() {
    const {t} = useTranslation();
    const dispatch = useDispatch();

    const [referralLink, setReferralLink] = useState();
    const [activeTab, setActiveTab] = useState('promos'); // 'promos' или 'drops'
    const [bonusPositions, setBonusPositions] = useState([]);
    const [bonusExpiresAt, setBonusExpiresAt] = useState(null);
    const [showProfileSheet, setShowProfileSheet] = useState(false);
    const [homeScreenStatus, setHomeScreenStatus] = useState('unknown');
    const isSelectingPromoType = useSelector((state) => state.promoSelectMode.isSelectingPromoType);
    const currentPage = useSelector((state) => state.page);
    const tg = useTelegram();
    const {startParam, refCode, initData} = tg;
    
    const getReferralLink = async ()=>{
        try {
            const response = await referralApi.getReferralLink();
            setReferralLink(response)
        } catch (e){
            console.warn('не получилось получить реферальную ссылку')
        }
    }
    
    // Функция загрузки бонусных позиций
    const loadBonusPositions = useCallback(() => {
        bonusApi.checkMy()
            .then((res) => {
                console.log('[HomeLayout] checkMy response:', res);
                if (res?.eligible && Array.isArray(res?.availablePositions)) {
                    setBonusPositions(res.availablePositions);
                    setBonusExpiresAt(
                        res?.expiresAt || res?.availableUntil || res?.validUntil || null
                    );
                } else {
                    setBonusPositions([]);
                    setBonusExpiresAt(null);
                }
            })
            .catch(() => {
                setBonusPositions([]);
                setBonusExpiresAt(null);
            });
    }, []);
    
    // Логи убраны намеренно: initData содержит подпись и hash сессии Telegram —
    // в пределах окна свежести его можно переиграть. В консоль такое не пишем.
    
    // Check home screen status for profile sheet
    useEffect(() => {
        const webApp = window.Telegram?.WebApp;
        if (webApp && typeof webApp.checkHomeScreenStatus === 'function') {
            webApp.checkHomeScreenStatus((status) => setHomeScreenStatus(status));
        }

        const onAdded = () => setHomeScreenStatus('added');
        if (webApp && typeof webApp.onEvent === 'function') {
            webApp.onEvent('homeScreenAdded', onAdded);
        }
        return () => {
            if (webApp && typeof webApp.offEvent === 'function') {
                webApp.offEvent('homeScreenAdded', onAdded);
            }
        };
    }, []);

    const handleAddToHomeScreen = useCallback(() => {
        const webApp = window.Telegram?.WebApp;
        if (webApp && typeof webApp.addToHomeScreen === 'function') {
            webApp.addToHomeScreen();
        }
    }, []);

    // Загрузка при монтировании
    useEffect(()=>{
        console.log('home')
        getReferralLink()
        loadBonusPositions();
    },[loadBonusPositions])
    
    // Перезагрузка бонусных позиций при возврате на Home (page.id === 0)
    useEffect(() => {
        if (currentPage?.id === 0 || currentPage?.name === 'Home') {
            console.log('[HomeLayout] Returned to Home, refreshing bonus positions');
            loadBonusPositions();
        }
    }, [currentPage, loadBonusPositions])

    const handleSelectPromoType = (pageId, name) => {
        // Переходим сразу в нужный тип промо
        dispatch(setPage({ id: pageId, name }));
        // Выключаем режим выбора
        dispatch(setSelectingPromoType(false));
    };

    const handleSegmentChange = (key) => {
        if (key === 'promo') {
            // остаёмся на домашнем экране
            return;
        }

        if (key === 'crystals') {
            // переходим на страницу Game (page id 4)
            dispatch(setPage({ id: 4, name: 'Game' }));
        }
    };

    return (
        <div className="w-full flex flex-col pt-4 pb-[110px]">

            <div className="w-full flex flex-row mt-[24px] items-center justify-between px-[15px]">
                {/* Центрируем SegmentSwitch по горизонтали */}
                <div className="flex-1 flex justify-center">
                    <SegmentSwitch onChange={handleSegmentChange}/>
                </div>
                {/* More icon → Overview */}
                <div className="relative">
                    <img
                        src={more}
                        alt="More"
                        className="w-[28px] h-[28px] cursor-pointer"
                        onClick={() => dispatch(setPage({ id: 10, name: 'Overview' }))}
                    />
                </div>
            </div>
            <Profile onAvatarClick={() => setShowProfileSheet(true)} />
            <div className="w-full pt-[22px] flex flex-col">
                <div className={'px-[16px]'}>
                    <div className={'flex flex-row h-[20px] gap-[20px] justify-between items-center w-full'}>
                        <div className={'flex flex-row h-[20px] gap-[20px]'}>
                            <span 
                                className={`text-[16px] font-semibold cursor-pointer ${
                                    activeTab === 'promos' ? 'text-white' : 'text-[#959595]'
                                }`}
                                onClick={() => setActiveTab('promos')}
                            >
                                Promos
                            </span>
                            <span 
                                className={`text-[16px] font-semibold cursor-pointer ${
                                    activeTab === 'drops' ? 'text-white' : 'text-[#959595]'
                                }`}
                                onClick={() => setActiveTab('drops')}
                            >
                                Drops
                            </span>
                        </div>
                        {/* Обёртка для кнопки и popup выбора типа промо */}
                        <div className="relative">
                            <NewPromoButton text={'create new promo'}/>

                            {isSelectingPromoType && (
                                <div className="absolute right-0 mt-2 z-20 w-[155px] rounded-[14px] bg-[#1B1B1D]/95 border border-white/15 shadow-[0_12px_30px_rgba(0,0,0,0.6)] px-[10px] py-[6px]">
                                    <div className="flex flex-row justify-between gap-[4px]">
                                        <button
                                            className="w-[32px] h-[32px] rounded-[8px] bg-white/5 hover:bg-white/10 border border-white/10 flex flex-col items-center justify-center gap-[2px] transition-colors"
                                            onClick={() => handleSelectPromoType(1, 'Crystals promo')}
                                        >
                                            <img src={CrystalsIcon} alt="Crystals" className="w-[14px] h-[14px] object-contain" />
                                            <span className="text-[8px] text-white font-semibold leading-[10px]">Crystals</span>
                                        </button>
                                        <button
                                            className="w-[32px] h-[32px] rounded-[8px] bg-white/5 hover:bg-white/10 border border-white/10 flex flex-col items-center justify-center gap-[2px] transition-colors"
                                            onClick={() => handleSelectPromoType(2, 'Crypto & Crystals')}
                                        >
                                            <img src={CCIcon} alt="C&C" className="w-[14px] h-[14px] object-contain" />
                                            <span className="text-[8px] text-white font-semibold leading-[10px]">C & C</span>
                                        </button>
                                        <button
                                            className="w-[32px] h-[32px] rounded-[8px] bg-white/5 hover:bg-white/10 border border-white/10 flex flex-col items-center justify-center gap-[2px] transition-colors"
                                            onClick={() => handleSelectPromoType(3, 'Crypto promo')}
                                        >
                                            <img src={CryptoIcon} alt="Crypto" className="w-[14px] h-[14px] object-contain" />
                                            <span className="text-[8px] text-white font-semibold leading-[10px]">Crypto</span>
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className={'w-full  flex mt-3 flex-row justify-between px-[20] text-[#D8D8D8] text-[11px]'}>
                        {activeTab === 'promos' ? (
                            <>
                                <span>category</span>
                                <div className={'flex flex-row gap-4'}>
                                    <span>players / winners</span>
                                    <span>prize pool</span>
                                </div>
                            </>
                        ) : (
                            <>
                                <span>category</span>
                                
                                    <span>chance</span>
                                    <span>reward</span>
                               
                            </>
                        )}
                    </div>
                </div>
                <div className={'w-full border-b mt-[6px] border-[#404040]'}/>
                
                {/* Бонусные баннеры — карусель, свайп между карточками */}
                {activeTab === 'promos' && bonusPositions.length > 0 && (
                    <BonusCarousel positions={bonusPositions} expiresAt={bonusExpiresAt} />
                )}

                {activeTab === 'promos' ? (
                    <PromoList />
                ) : (
                    <div className="w-full flex items-center justify-center min-h-[200px]">
                        <Drops/>
                    </div>
                )}
            </div>

            {/* Profile Bottom Sheet */}
            {showProfileSheet && (
                <div
                    className="fixed inset-0 z-50 flex items-end justify-center"
                    style={{ background: 'rgba(0,0,0,0.65)' }}
                    onClick={() => setShowProfileSheet(false)}
                >
                    <div
                        className="w-full rounded-t-[20px] overflow-hidden animate-slide-up"
                        style={{ background: '#1A1A1A' }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Drag handle */}
                        <div className="flex justify-center pt-3 pb-4">
                            <div className="w-[36px] h-[4px] rounded-full bg-[#3B3B3B]" />
                        </div>

                        {/* Menu items */}
                        <div className="flex flex-col px-4 pb-2">
                            {/* Profile Settings */}
                            <button
                                className="w-full h-[52px] flex items-center gap-3 px-3 rounded-[12px] active:bg-white/5 transition-colors"
                                onClick={() => setShowProfileSheet(false)}
                            >
                                <div className="w-[36px] h-[36px] rounded-[10px] bg-[#2C2C2C] flex items-center justify-center">
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#EAEAEA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                                        <circle cx="12" cy="7" r="4"/>
                                    </svg>
                                </div>
                                <div className="flex flex-col items-start">
                                    <span className="text-white text-[15px] font-medium">Profile Settings</span>
                                    <span className="text-[#959595] text-[12px]">Coming soon</span>
                                </div>
                            </button>

                            <div className="w-full h-px mx-3" style={{ background: '#2C2C2C' }} />

                            {/* Notifications */}
                            <button
                                className="w-full h-[52px] flex items-center gap-3 px-3 rounded-[12px] active:bg-white/5 transition-colors"
                                onClick={() => setShowProfileSheet(false)}
                            >
                                <div className="w-[36px] h-[36px] rounded-[10px] bg-[#2C2C2C] flex items-center justify-center">
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#EAEAEA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
                                        <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
                                    </svg>
                                </div>
                                <div className="flex flex-col items-start">
                                    <span className="text-white text-[15px] font-medium">Notifications</span>
                                    <span className="text-[#959595] text-[12px]">Coming soon</span>
                                </div>
                            </button>

                            <div className="w-full h-px mx-3" style={{ background: '#2C2C2C' }} />

                            {/* Add to Home Screen */}
                            {homeScreenStatus !== 'added' && (
                                <>
                                    <button
                                        className="w-full h-[52px] flex items-center gap-3 px-3 rounded-[12px] active:bg-white/5 transition-colors"
                                        onClick={() => {
                                            handleAddToHomeScreen();
                                            setShowProfileSheet(false);
                                        }}
                                    >
                                        <div className="w-[36px] h-[36px] rounded-[10px] bg-[#2C2C2C] flex items-center justify-center">
                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#2B8FF4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
                                                <polyline points="9 22 9 12 15 12 15 22"/>
                                            </svg>
                                        </div>
                                        <span className="text-[#2B8FF4] text-[15px] font-medium">Add to Home Screen</span>
                                    </button>
                                    <div className="w-full h-px mx-3" style={{ background: '#2C2C2C' }} />
                                </>
                            )}

                            {/* Support */}
                            <button
                                className="w-full h-[52px] flex items-center gap-3 px-3 rounded-[12px] active:bg-white/5 transition-colors"
                                onClick={() => setShowProfileSheet(false)}
                            >
                                <div className="w-[36px] h-[36px] rounded-[10px] bg-[#2C2C2C] flex items-center justify-center">
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#EAEAEA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <circle cx="12" cy="12" r="10"/>
                                        <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
                                        <line x1="12" y1="17" x2="12.01" y2="17"/>
                                    </svg>
                                </div>
                                <div className="flex flex-col items-start">
                                    <span className="text-white text-[15px] font-medium">Support</span>
                                    <span className="text-[#959595] text-[12px]">Coming soon</span>
                                </div>
                            </button>
                        </div>

                        {/* Safe area bottom padding */}
                        <div className="h-[34px]" />
                    </div>
                </div>
            )}
        </div>
    );
}
